// Engine Registry for Peak Priorities Elevation
// Manages all engines and provides unified access

import { BaseEngine } from './BaseEngine';
import { ProspectorEngine } from './ProspectorEngine';
import { KnowledgeEngine } from './KnowledgeEngine';

export class EngineRegistry {
  private engines: Map<string, BaseEngine>;
  private static instance: EngineRegistry;

  private constructor() {
    this.engines = new Map();
    this.initializeEngines();
  }

  public static getInstance(): EngineRegistry {
    if (!EngineRegistry.instance) {
      EngineRegistry.instance = new EngineRegistry();
    }
    return EngineRegistry.instance;
  }

  private initializeEngines(): void {
    // Initialize all engines
    this.engines.set('prospector_engine', new ProspectorEngine());
    this.engines.set('knowledge_engine', new KnowledgeEngine());
    
    // Add more engines as they are implemented
    // this.engines.set('curation_engine', new CurationEngine());
    // this.engines.set('canon_engine', new CanonEngine());
    // this.engines.set('authoring_engine', new AuthoringEngine());
    // this.engines.set('studio_engine', new StudioEngine());
    // this.engines.set('assembly_engine', new AssemblyEngine());
    // this.engines.set('publishing_engine', new PublishingEngine());
  }

  // Get a specific engine
  public getEngine(engineId: string): BaseEngine | undefined {
    return this.engines.get(engineId);
  }

  // Get all engines
  public getAllEngines(): BaseEngine[] {
    return Array.from(this.engines.values());
  }

  // Execute operation on specific engine
  public async executeOperation(engineId: string, operationId: string, input: any): Promise<any> {
    const engine = this.getEngine(engineId);
    if (!engine) {
      throw new Error(`Engine ${engineId} not found`);
    }
    return await engine.executeOperation(operationId, input);
  }

  // Get all operations for a specific engine
  public getEngineOperations(engineId: string) {
    const engine = this.getEngine(engineId);
    return engine ? engine.getOperations() : [];
  }

  // Get engines that support a specific phase
  public getEnginesForPhase(phaseId: string): BaseEngine[] {
    const phaseEngineMapping: Record<string, string[]> = {
      'gathering': ['prospector_engine'],
      'tagging': ['knowledge_engine'],
      'formulating': ['curation_engine', 'canon_engine'],
      'creating': ['authoring_engine', 'studio_engine'],
      'applying': ['assembly_engine'],
      'publishing': ['publishing_engine']
    };

    const engineIds = phaseEngineMapping[phaseId] || [];
    return engineIds
      .map(id => this.getEngine(id))
      .filter(engine => engine !== undefined) as BaseEngine[];
  }

  // Get system status
  public getSystemStatus() {
    return {
      totalEngines: this.engines.size,
      engines: Array.from(this.engines.entries()).map(([id, engine]) => ({
        id,
        name: engine.getInfo().name,
        purpose: engine.getInfo().purpose,
        operationCount: engine.getOperations().length
      }))
    };
  }
}
